import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, X, Loader2 } from "lucide-react";

const MAX = 20 * 1024 * 1024;
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export function AttachmentPicker({ value = [], onChange, testId = "attachments" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const total = value.reduce((s, a) => s + (a.size || 0), 0);

  const onPick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    if (total + files.reduce((s, f) => s + f.size, 0) > MAX) return toast.error("Attachments must be 20 MB or less in total");
    setBusy(true);
    try {
      const added = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ file_id: r.data.file_id, original_name: r.data.original_name, size: r.data.size });
      }
      onChange([...value, ...added]);
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-3" data-testid={testId}>
      <div className="flex flex-wrap gap-2 items-center">
        {value.map((a) => (
          <span key={a.file_id} className="tt-badge-neutral flex items-center gap-1.5 !normal-case" data-testid={`${testId}-chip`}>
            <Paperclip size={11} /> {a.original_name} <span className="text-[#71717A]">{fmtSize(a.size || 0)}</span>
            <button type="button" onClick={() => onChange(value.filter((x) => x.file_id !== a.file_id))} aria-label="Remove attachment" data-testid={`${testId}-remove`}><X size={11} /></button>
          </span>
        ))}
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="tt-btn-ghost text-xs flex items-center gap-1.5" data-testid={`${testId}-add`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />} {busy ? "Uploading…" : "Attach files"}
        </button>
        <input ref={ref} type="file" multiple className="hidden" onChange={onPick} data-testid={`${testId}-input`} />
      </div>
      {value.length > 0 && <div className="text-[11px] text-[#71717A] mt-1">{fmtSize(total)} of 20 MB. On WhatsApp, files go in the chat if the candidate wrote in the last 24h, otherwise as a secure download link.</div>}
    </div>
  );
}
